import gql from 'graphql-tag'

import { useQuery } from '@apollo/react-hooks'
import {
  BasketData,
  BasketVariables,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Basket/Basket'
import {
  Country,
  Currency,
  Feature,
} from '@thg-commerce/enterprise-network/src/generated/graphql'

type UseCartObjectProps = {
  useCart: boolean
  currency: string
  shippingDestination: string
  enableWishlists?: boolean
  enableWishlistOnBasketItem?: boolean
  enableWishlistsGlobal?: boolean
  productDescriptionKeys?: {
    items: string[]
    details: string[]
    download: string[]
    video: string[]
  }
  horizonFeatures?: Feature[]
  productDetailKeyAdditionList?: string[]
  basketId?: string
}
const BASKET = gql`
  query Basket(
    $id: ID
    $settings: SessionSettings!
    $currency: Currency!
    $shippingDestination: Country!
    $productContentKeys: [String!]
    $wishlistEnabled: Boolean
    $loyaltyEnabled: Boolean
  ) {
    basket(
      id: $id
      currency: $currency
      shippingDestination: $shippingDestination
      productContentKeys: $productContentKeys
      wishlistEnabled: $wishlistEnabled
      loyaltyEnabled: $loyaltyEnabled
    ) @client {
      totalQuantity
      standardPrice {
        amount
      }
      items {
        product {
          sku
          product {
            sku
            variants {
              sku
            }
          }
        }
        quantity
        totalStandardPrice {
          amount
        }
        standardPricePerUnit {
          amount
        }
      }
    }
  }
`

export const useCartObject = ({
  useCart,
  currency,
  shippingDestination,
  horizonFeatures,
  productDescriptionKeys,
  productDetailKeyAdditionList,
  basketId,
  enableWishlists,
  enableWishlistOnBasketItem,
  enableWishlistsGlobal,
}: UseCartObjectProps) => {
  const { data, loading } = useQuery<{ basket: BasketData }, BasketVariables>(
    BASKET,
    {
      ssr: typeof window !== 'undefined',
      skip: !basketId,
      variables: {
        currency: currency as Currency,
        shippingDestination: shippingDestination as Country,
        loyaltyEnabled: horizonFeatures?.includes(Feature.Loyalty) || false,
        wishlistEnabled:
          (enableWishlists &&
            enableWishlistsGlobal &&
            enableWishlistOnBasketItem &&
            horizonFeatures?.includes(Feature.Wishlist)) ||
          false,
        id: basketId,
        productContentKeys: [
          ...(productDescriptionKeys?.items || []),
          ...(productDescriptionKeys?.details || []),
          ...(productDescriptionKeys?.video || []),
          ...(productDetailKeyAdditionList || []),
        ],
      },
    },
  )

  if (!basketId) {
    return { loading: false, cart: null }
  }

  if (!useCart) {
    return { loading: false, cart: null }
  }

  return data && !loading
    ? {
        loading,
        cart: {
          id: null,
          items: data.basket.items.map((item) => ({
            quantity: item.quantity,
            product_group: {
              id: Number(item.product.product?.sku),
              selected_variant: {
                price: {
                  currency,
                  value: Number(item.standardPricePerUnit.amount),
                },
                sku: Number(item.product.sku),
              },
              total_variants: Number(item.product.product?.variants.length),
            },
          })),
          total_price: {
            currency,
            value: Number(data.basket.standardPrice.amount),
          },
          total_size: data.basket.totalQuantity,
        },
      }
    : { loading, cart: null }
}
